/**
 * Tiny hash router. Each page exports `mount(root, params)` to build itself into
 * the root element and `unmount()` to release listeners + DOM. The router calls
 * unmount on the previous page before mounting the next, so each page owns its
 * own teardown and HMR doesn't accumulate ghosts.
 *
 * No history API, no server-side routing concern (fragments don't hit the server).
 */

export interface PageModule {
  mount(root: HTMLElement, params: Record<string, string>): void;
  unmount(): void;
}

export interface Route {
  /** Return params on match, null on no-match. */
  match(hash: string): Record<string, string> | null;
  /** Lazy-loads the page module so each page is its own chunk. */
  load(): Promise<PageModule>;
}

export function startRouter(root: HTMLElement, routes: Route[], fallbackHash: string): void {
  let active: PageModule | null = null;
  let dispatchToken = 0;

  async function dispatch(): Promise<void> {
    const token = ++dispatchToken;
    const hash = window.location.hash;
    for (const route of routes) {
      const params = route.match(hash);
      if (!params) continue;
      const page = await route.load();
      // A faster hashchange may have superseded us while the dynamic import was in flight.
      if (token !== dispatchToken) return;
      if (active) active.unmount();
      active = page;
      page.mount(root, params);
      return;
    }
    if (hash === fallbackHash) return; // already at the fallback; nothing routed it.
    window.location.replace(`${window.location.pathname}${window.location.search}${fallbackHash}`);
  }

  window.addEventListener('hashchange', () => { void dispatch(); });
  void dispatch();
}
